import { Avatar, AvatarFallback, AvatarImage, cn } from "@ibada/ui"

/** Two-letter initials from a workspace name: first letters of the first two words, or the first two
 * characters of a single word; "?" when there's nothing usable. */
function getWorkspaceInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return "?"
  const initials =
    words.length === 1
      ? (words[0] ?? "").slice(0, 2)
      : words.slice(0, 2).map((word) => word[0] ?? "").join("")
  return initials.toUpperCase() || "?"
}

type WorkspaceAvatarProps = {
  name: string
  src?: string | null
  size?: "default" | "sm" | "lg"
  className?: string
  fallbackClassName?: string
}

/**
 * Avatar for a workspace/organization: its logo when set, otherwise INITIALS from the name (not the generic
 * person silhouette that {@link UserAvatar} uses). Sizing goes through the reliable `size` variant.
 */
export function WorkspaceAvatar({ name, src, size = "default", className, fallbackClassName }: WorkspaceAvatarProps) {
  return (
    <Avatar size={size} className={className}>
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback className={cn("bg-primary/10 text-primary text-xs font-semibold", fallbackClassName)}>
        {getWorkspaceInitials(name)}
      </AvatarFallback>
    </Avatar>
  )
}
